import LumeAvatar from './LumeAvatar';
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, ArrowLeftRight, CalendarClock, CreditCard,
  Landmark, BarChart3, MessageSquareShare, Settings, LogOut, Bell, Target, ShieldCheck, Scale, FileText
} from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import ThemeToggle from "./ThemeToggle";

const items = [
  { to: "/dashboard", label: "Visão Geral", icon: LayoutDashboard, testId: "nav-item-dashboard" },
  { to: "/transactions", label: "Lançamentos", icon: ArrowLeftRight, testId: "nav-item-transactions" },
  { to: "/planning", label: "Planejamentos", icon: Target, testId: "nav-item-planning" },
  { to: "/bills", label: "Contas a Pagar/Receber", icon: CalendarClock, testId: "nav-item-bills" },
  { to: "/cards", label: "Cartões", icon: CreditCard, testId: "nav-item-cards" },
  { to: "/accounts", label: "Contas Bancárias", icon: Landmark, testId: "nav-item-accounts" },
  { to: "/reconciliation", label: "Conferir Caixa e Bancos", icon: Scale, testId: "nav-item-reconciliation" },
  { to: "/reports", label: "Relatórios", icon: BarChart3, testId: "nav-item-reports" },
  { to: "/notas-fiscais", label: "Notas Fiscais", icon: FileText, testId: "nav-item-fiscal", module: "fiscal_invoices" },
  { to: "/assistant", label: "Assistente LUME", icon: MessageSquareShare, testId: "nav-item-assistant", highlight: true },
  { to: "/alerts", label: "Alertas", icon: Bell, testId: "nav-item-alerts", alerts: true },
  { to: "/settings", label: "Configurações", icon: Settings, testId: "nav-item-settings" },
];

export default function Sidebar({ open = false }) {
  const { user, workspace, logout } = useAuth();
  const location = useLocation();
  const [alertCount, setAlertCount] = useState(0);
  useEffect(() => {
    const refresh = () => api.get("/alerts").then((r) => setAlertCount(r.data.count || 0)).catch(() => {});
    refresh();
    const timer = window.setInterval(refresh, 60000);
    return () => window.clearInterval(timer);
  }, [location.pathname]);
  return (
    <aside
      id="app-sidebar"
      className={`hidden md:flex flex-col w-[260px] bg-surface-subtle border-r border-white/5 h-screen fixed top-0 left-0 z-50 p-5 pt-20 overflow-y-auto shadow-2xl transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full invisible"}`}
      aria-hidden={!open}
      data-testid="app-sidebar"
    >
      <Logo />
      <nav className="mt-8 flex-1 space-y-1">
        {[...items.filter((it) => !it.module || workspace?.modules?.[it.module]?.enabled), ...(user?.is_platform_admin ? [{ to: "/admin", label: "Administração", icon: ShieldCheck, testId: "nav-item-admin" }] : [])].map((it) => {
          const Icon = it.icon;
          const active = location.pathname.startsWith(it.to);
          return (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors
                ${active
                  ? "bg-[rgba(0,229,255,0.12)] text-cyan border border-[rgba(0,229,255,0.35)]"
                  : "text-white/70 hover:text-white hover:bg-white/5 border border-transparent"}`}
            >
              {it.to === "/assistant" ? <LumeAvatar size={30} /> : <Icon size={18} />}
              <span>{it.label}</span>
              {it.highlight && !active && (
                <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-[rgba(0,229,255,0.15)] text-cyan">IA</span>
              )}
              {it.alerts && alertCount > 0 && <span className="ml-auto min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{alertCount > 99 ? "99+" : alertCount}</span>}
            </NavLink>
          );
        })}
      </nav>
      <div className="mt-4 pt-4 border-t border-white/5">
        <ThemeToggle inline />
        <div className="text-xs text-muted mb-1">Sessão</div>
        <div className="text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="text-xs text-muted mb-3">{workspace?.name}</div>
        <button
          onClick={logout}
          data-testid="btn-logout"
          className="w-full flex items-center gap-2 text-sm text-white/70 hover:text-danger transition-colors"
        >
          <LogOut size={16} /> Sair
        </button>
      </div>
    </aside>
  );
}


